import { useAnalyticsStore } from '@/stores/analytics';
import { resolveAnalyticsClickTarget } from '@/utils/analytics-click';
import { ANALYTICS_LOG_SCHEMA_VERSION } from '@/utils/analytics-log';
import { trackAnalyticsLog, beginAnalyticsFlow, endAnalyticsFlow } from '@/utils/analytics-track';
import {
  captureAcquisitionFromRoute,
  consumeAnalyticsLoginMethod,
  consumeInviteLandPending,
  getAcquisitionProps,
  hasTrackedAcquisitionLand,
  hasTrackedInviteLand,
  markAcquisitionLandTracked,
  markInviteLandTracked,
  withAcquisitionProps,
} from '@/utils/analytics-acquisition';
import { resolveMarketOpenTrackPayload } from '@/utils/analytics-market-open';
import { setupVisitEngaged10s } from '@/utils/analytics-visit-engaged';
import { setupLaTrackOutbox } from '@/utils/la-track';
import { sweepLaOutbox } from '@/utils/la-outbox';
import { subscribeAuthState } from '@/utils/auth-session';
import { getUserId } from '@/utils/auth-token';

let setupDone = false;

const HELP_ROUTE_NAMES = new Set(['Help', 'HelpDocument', 'HelpMarketRules']);
const ROUTE_ENTER_EVENT_BY_NAME = Object.freeze({
  UserProfile: { key: 'profile.open', label: '打开个人主页' },
  AccountPoints: { key: 'points.open', label: '打开积分页' },
  Account: { key: 'account.open', label: '打开账户页' },
  Referrals: { key: 'invite.open', label: '打开邀请好友页面' },
  Sport: { key: 'sport.list_enter', label: '进入体育列表页' },
  Crypto: { key: 'crypto.list_enter', label: '进入加密市场列表页' },
  Leagues: { key: 'leagues.list_enter', label: '进入排行榜页' },
});

/**
 * 路由级业务埋点（帮助 / 重点页面 / 体育详情），与 page.enter 互补。
 * @param {{ name?: unknown, params?: Record<string, unknown> }} to
 * @param {{ name?: unknown, params?: Record<string, unknown> }} from
 */
function trackScopedRouteAnalytics(to, from) {
  const toName = typeof to?.name === 'string' ? to.name : '';
  const fromName = typeof from?.name === 'string' ? from.name : '';
  if (!toName) return;

  const routeEnterEvent = ROUTE_ENTER_EVENT_BY_NAME[toName];
  const toProfileId = toName === 'UserProfile' ? String(to?.params?.userId ?? '').trim() : '';
  const fromProfileId = fromName === 'UserProfile' ? String(from?.params?.userId ?? '').trim() : '';
  if (routeEnterEvent && (toName !== fromName || toProfileId !== fromProfileId)) {
    trackAnalyticsLog({
      key: routeEnterEvent.key,
      label: routeEnterEvent.label,
      value: {
        routeName: toName,
        userId: toProfileId || undefined,
      },
    });
  }

  if (HELP_ROUTE_NAMES.has(toName)) {
    const toSlug = String(to?.params?.slug ?? '').trim();
    const fromSlug = String(from?.params?.slug ?? '').trim();
    if (toName !== fromName || toSlug !== fromSlug) {
      trackAnalyticsLog({
        key: 'help.open',
        label: '打开帮助',
        value: {
          routeName: toName,
          slug: toName === 'HelpDocument' ? toSlug || undefined : undefined,
        },
      });
      if (toName === 'HelpDocument' && toSlug) {
        trackAnalyticsLog({
          key: 'help.doc_open',
          label: '打开帮助文档',
          value: { slug: toSlug },
        });
      }
    }
  }

  if (toName === 'SportLiveMatchDetail' && toName !== fromName) {
    const matchId = String(to?.params?.matchId ?? '').trim();
    trackAnalyticsLog({
      key: 'sport.live_enter',
      label: '进入体育直播详情',
      value: { matchId: matchId || undefined },
    });
  }

  if (toName === 'SportFuturesMarketDetail' && toName !== fromName) {
    const marketId = String(to?.params?.marketId ?? '').trim();
    trackAnalyticsLog({
      key: 'sport.futures_enter',
      label: '进入体育期货详情',
      value: { marketId: marketId || undefined },
    });
  }

  if (toName === 'TrialFundSim' && toName !== fromName) {
    const side = String(to?.query?.side ?? '').trim();
    trackAnalyticsLog({
      key: 'market.open_newbie',
      label: '新手盘口点击',
      value: {
        source: fromName || 'direct',
        side: side === 'yes' || side === 'no' ? side : undefined,
      },
    });
  }

  if (toName === fromName) return;

  // 分钟盘详情（与 market.open 互补）
  if (
    toName === 'CryptoUpdownDetail' ||
    toName === 'CryptoUpdownLiveDetail' ||
    String(to?.path || '').includes('updown')
  ) {
    trackAnalyticsLog({
      key: 'updown.enter',
      label: '进入分钟盘',
      value: {
        routeName: toName,
        id: String(to?.params?.id ?? '').trim() || undefined,
      },
    });
  }
}

/**
 * 在 createPinia + app.use(router) 之后调用。
 * @param {import('vue-router').Router} router
 */
export function setupAnalytics(router) {
  if (setupDone || typeof window === 'undefined') return;
  setupDone = true;

  const analytics = useAnalyticsStore();
  void analytics.hydrate().then(() => {
    const liveUserId = getUserId() || '';
    if (liveUserId) analytics.setUserId(liveUserId);
  });

  let wasLoggedIn = Boolean(getUserId());
  subscribeAuthState((loggedIn) => {
    if (loggedIn) {
      const nextId = getUserId() || '';
      analytics.setUserId(nextId);
      if (!wasLoggedIn) {
        const durationMs = endAnalyticsFlow('login');
        const method = consumeAnalyticsLoginMethod();
        trackAnalyticsLog({
          key: 'login.success',
          label: '登录成功',
          value: withAcquisitionProps({
            userId: nextId || undefined,
            method: method || undefined,
          }),
          durationMs,
        });
      }
      wasLoggedIn = true;
      return;
    }
    if (wasLoggedIn) {
      trackAnalyticsLog({
        key: 'login.logout',
        label: '登出',
        value: { userId: analytics.userId || undefined },
      });
      analytics.setUserId('');
      void analytics.persistNow();
    }
    wasLoggedIn = false;
  });

  router.afterEach((to, from) => {
    captureAcquisitionFromRoute(to);
    if (!hasTrackedAcquisitionLand()) {
      markAcquisitionLandTracked();
      trackAnalyticsLog({
        key: 'acquisition.land',
        label: '落地',
        value: getAcquisitionProps(),
      });
    }

    // 只认 /r/:inviteCode、?invite=、?inviteCode=；手填邀请码不打
    if (consumeInviteLandPending() && !hasTrackedInviteLand()) {
      markInviteLandTracked();
      trackAnalyticsLog({
        key: 'invite.land',
        label: '从邀请链接进来',
        value: withAcquisitionProps({ channel: 'invite' }),
      });
    }

    const name = typeof to.name === 'string' && to.name ? to.name : String(to.path || '');
    analytics.enterRoute(name);

    const marketOpen = resolveMarketOpenTrackPayload(to, from);
    if (marketOpen) {
      trackAnalyticsLog({
        key: 'market.open',
        label: '真实盘口点击',
        value: marketOpen,
      });
    }

    trackScopedRouteAnalytics(to, from);
    sweepLaOutbox();
  });

  document.addEventListener(
    'click',
    (event) => {
      if (!analytics.routeName) {
        const n = router.currentRoute.value?.name;
        if (typeof n === 'string' && n) analytics.routeName = n;
      }
      const info = resolveAnalyticsClickTarget(event.target);
      if (!info) return;
      trackAnalyticsLog({
        key: 'ui.click',
        label: info.label || info.tag || 'click',
        value: info,
      });
    },
    true
  );

  document.addEventListener('visibilitychange', () => {
    analytics.setPageVisible(document.visibilityState === 'visible');
    if (document.visibilityState === 'visible') {
      void analytics.runRetentionAndArchive();
      sweepLaOutbox();
    }
  });

  window.addEventListener('pagehide', () => {
    analytics.setPageVisible(false);
    void analytics.persistNow();
  });

  setupLaTrackOutbox();
  setupVisitEngaged10s(router);

  if (process.env.NODE_ENV === 'development') {
    window.__dsAnalytics = () => analytics.snapshot;
    window.__dsAnalytics.schema = ANALYTICS_LOG_SCHEMA_VERSION;
    window.__dsAnalytics.archives = () => analytics.archiveSummaries;
  }
}

export { beginAnalyticsFlow, endAnalyticsFlow, trackAnalyticsLog };
